import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import IconifyIcon from 'components/base/IconifyIcon';
import { useAuth } from 'providers/AuthProvider';
import { resetSandbox, endSandbox } from 'services/api/sandbox';
import { hasRealSession } from 'utils/sessionStore';
import { weddingPaths } from 'routes/paths';

/**
 * Persistent bar shown on every couple surface while a "Try the full plan"
 * sandbox is active. Self-hides otherwise. Gated on `role === 'couple' &&
 * isSandbox` so it never renders for a guest session (guests inherit the
 * couple's plan, but never the sandbox flag).
 *
 * A quiet, neutral strip under the app bar: a state notice, not a warning,
 * so no orange slab. The Heirloom crown is the one warm mark; the way out of
 * the demo is the one tinted button, and reset and exit say what they do.
 */
const SandboxBanner = () => {
  const { sessionUser, exitSandbox } = useAuth();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  // On a phone, 36px: small 30px buttons side by side are easy to mis-tap.
  const tapTarget = { minHeight: { xs: 36, md: 'auto' } };
  // Their labels say it all; without the icons both fit one row on a 360px phone.
  const utilitySx = {
    ...tapTarget,
    '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'inherit' } },
  };

  if (!(sessionUser?.role === 'couple' && sessionUser?.isSandbox)) {
    return null;
  }

  const handleExit = async () => {
    setBusy(true);
    await endSandbox(); // best-effort teardown; the sweep reaps anything left
    // exitSandbox clears the sandbox scope and hard-reloads into the real session
    // (or the login page for an anonymous visitor). The full reload is what tears
    // down all demo-scoped page state — a client-side navigate would leave it.
    exitSandbox();
  };

  const handleReset = async () => {
    setBusy(true);
    try {
      await resetSandbox();
      enqueueSnackbar(t('sandbox.reset_success'), { variant: 'success' });
      navigate(0); // reload the current route so it refetches the fresh sample data
    } catch {
      enqueueSnackbar(t('sandbox.start_error'), { variant: 'error' });
      setBusy(false);
    }
  };

  const handleUpgrade = () => {
    // A real couple behind the demo → their billing page; an anonymous visitor →
    // the real create-event flow. exitSandbox leaves the demo and hard-reloads
    // straight to the destination so no demo state bleeds into the real session.
    const dest = hasRealSession()
      ? weddingPaths.coupleBilling
      : `${weddingPaths.coupleCreateEvent}?plan=heirloom`;
    exitSandbox(dest);
  };

  return (
    <Box
      sx={{
        position: 'sticky',
        // Pin flush beneath the fixed AppBar so the two bars never overlap on
        // scroll. Offset = the `appbar` Toolbar variant height (64px on xs/sm,
        // 82px from md up — see theme/components/Toolbar.tsx).
        top: { xs: 64, md: 82 },
        zIndex: (theme) => theme.zIndex.appBar + 1,
        bgcolor: 'background.elevation2',
        color: 'text.primary',
        borderBottom: 1,
        borderColor: 'divider',
        px: { xs: 2, md: 5 },
        py: { xs: 1.5, md: 1.25 },
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 1.25, md: 3 }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
          <IconifyIcon
            icon="solar:crown-linear"
            sx={{ fontSize: 20, flexShrink: 0, mt: 0.25, color: 'warning.main' }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.4 }}>
              {t('sandbox.banner.title')}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
              {t('sandbox.banner.subtitle')}
            </Typography>
          </Box>
        </Stack>
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
          sx={{ flexShrink: 0, pl: { xs: 4, md: 0 } }}
        >
          {/* The way out of the demo into a real event: the strip's one tinted button. */}
          <Button
            size="small"
            variant="soft"
            color="primary"
            onClick={handleUpgrade}
            disabled={busy}
            // The theme's soft text reads at 3.6:1 on this strip; one shade darker passes AA.
            sx={{ ...tapTarget, fontWeight: 600, mr: 0.5, color: 'primary.darker' }}
          >
            {t('sandbox.banner.upgrade_cta')}
          </Button>
          <Button
            size="small"
            variant="text"
            color="neutral"
            onClick={handleReset}
            disabled={busy}
            sx={utilitySx}
            startIcon={<IconifyIcon icon="solar:refresh-linear" sx={{ fontSize: 16 }} />}
          >
            {t('sandbox.banner.reset')}
          </Button>
          <Button
            size="small"
            variant="text"
            color="neutral"
            onClick={handleExit}
            disabled={busy}
            sx={utilitySx}
            startIcon={<IconifyIcon icon="solar:logout-2-linear" sx={{ fontSize: 16 }} />}
          >
            {t('sandbox.banner.exit')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default SandboxBanner;
